import { css } from '@emotion/react';
import { Chip, Stack } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { DescriptionIcon } from '../../../../../components/AgTable/cell-renderers/FieldNameRenderer';
import { MaxDataCollectionFileSize } from '../../../../../components/Form/Display/FieldKPIFileUpload2';
import { KpiSection } from '../../../../../data-models/company-financials.data-model';
import { IField, KpiTemplateSectionType } from '../../../../../data-models/field2.data-model';
import {
  IKPIRequestDataModel,
  IKPIRequestResponse,
  IKPIResponseFormSectionData,
  KPIRequestFrequency,
} from '../../../../../data-models/kpi-requests.data-model';
import {
  IKPITableDataModel,
  IKPITemplate,
  IKPITemplateSectionDataModel,
  ITextualKpiTemplateSectionMeta,
} from '../../../../../data-models/kpi-template.data-model';
import { MaggieFeatureFlags } from '../../../../../util/feature-flags';
import { IFormFieldTextMeta } from '../../../../../view-models/form.view-model';
import { SectionTitle } from '../../../../CompanyProfiles/Summary/components/SubSection';
import { KPIFieldFactory, KPIFieldFactoryProps } from '../../Sections/FormItem/KPIFieldFactory';
import { ITextualKpiFieldProps, TextualKpiField } from '../../Sections/FormItem/TextualKpiField';
import { IKPIGridSectionProps, KPIAgGrid } from './KPIAgGrid';
import { KPIAgGrid2 } from './KPIAgGrid2';

const kpiWrapperCss = css`
  display: flex;
  flex-direction: column;
  margin: 0 0 2rem 0;
  row-gap: 1rem;
`;

const kpiGridTitleCss = css`
  column-gap: 0.5rem;
  display: grid;
  grid-template-columns: max-content min-content;
  align-items: center;
`;

interface IKPIRenderedTemplateProps {
  onKPIGridDataUpdated: IKPIGridSectionProps['onKPIGridDataUpdated'];
  onSectionDataUpdated: KPIFieldFactoryProps['onChange'];
  onTextualKpiUpdated: ITextualKpiFieldProps['onChange'];
  request: IKPIRequestDataModel;
  response: IKPIRequestResponse;
  template: IKPITemplate;
  disabled?: boolean;
}

export function KPIRenderedTemplate(props: IKPIRenderedTemplateProps) {
  const {
    disabled,
    onKPIGridDataUpdated,
    onSectionDataUpdated,
    onTextualKpiUpdated,
    request,
    response,
    template,
  } = props;

  const { showKpiGridWithHistoricalDataSupport } = useFlags<MaggieFeatureFlags>();

  const sections = useMemo(() => {
    if (template) {
      return (
        template.sections
          ?.toSorted((section1, section2) => {
            return (section1.meta.sortOrder ?? -1) - (section2.meta.sortOrder ?? -1);
          })
          .map((section, index) => {
            if (section.type === 'grid') {
              const { frequency, fye, granularity, period } = request;
              const gridSectionMeta = section.meta as IKPITableDataModel;

              return (
                <div key={`${gridSectionMeta.section}-${section.id}`} css={kpiWrapperCss}>
                  <div css={kpiGridTitleCss}>
                    <SectionTitle title={`${index + 1}. ${gridSectionMeta.displayName}`} />
                    <Chip label={gridSectionMeta.section} color={'secondary'} />
                  </div>
                  {showKpiGridWithHistoricalDataSupport ? (
                    <KPIAgGrid2
                      companyId={request.companyId}
                      companyFYEDateString={fye}
                      disabled={disabled}
                      kpiRequestFrequency={frequency}
                      kpiRequestGranularity={granularity}
                      kpiRequestPeriod={period}
                      kpiResponseData={response.kpiData.values}
                      onKPIGridDataUpdated={onKPIGridDataUpdated}
                      section={section}
                    />
                  ) : (
                    <KPIAgGrid
                      companyId={request.companyId}
                      companyFYEDateString={fye}
                      disabled={disabled}
                      kpiRequestFrequency={frequency}
                      kpiRequestGranularity={granularity}
                      kpiRequestPeriod={period}
                      kpiResponseData={response.kpiData.values}
                      onKPIGridDataUpdated={onKPIGridDataUpdated}
                      section={section}
                    />
                  )}
                </div>
              );
            } else if (section.type === KpiTemplateSectionType.textualKpi) {
              const sectionMeta = section.meta as ITextualKpiTemplateSectionMeta;
              return (
                <TextualKpiSection
                  key={`${response?.id}-${section.id}`}
                  description={sectionMeta.kpiConfig.description}
                  displayName={`${index + 1}. ${sectionMeta.kpiConfig.displayName}`}
                  kpiSection={sectionMeta.section}
                  section={section}
                  frequency={request.frequency}
                  period={request.period}
                  onChange={onTextualKpiUpdated}
                  response={response}
                  disabled={disabled ?? false}
                />
              );
            } else {
              const sectionData: IKPIResponseFormSectionData = response.sectionData.find(
                (data) => data.sectionId === section.id
              ) ?? {
                sectionId: section.id ?? -1,
                value: null,
              };
              const description = getSectionDescription(section);

              return (
                <div key={section.id} css={kpiWrapperCss}>
                  <SectionTitle
                    title={`${index + 1}. ${(section.meta as IField<unknown>).entityField}`}
                    description={description}
                  />
                  <KPIFieldFactory
                    key={`${response.id}-${section.id}`}
                    companyId={request.companyId}
                    onChange={onSectionDataUpdated}
                    section={section}
                    sectionData={sectionData}
                    disabled={disabled}
                  />
                </div>
              );
            }
          }) ?? []
      );
    }
    return [];
  }, [
    template,
    request,
    showKpiGridWithHistoricalDataSupport,
    disabled,
    response,
    onKPIGridDataUpdated,
    onTextualKpiUpdated,
    onSectionDataUpdated,
  ]);
  return <>{sections}</>;
}

function KpiSectionHeader(props: { description: string | null; displayName: string; section?: KpiSection }) {
  const { description, displayName, section } = props;
  return (
    <div css={kpiGridTitleCss}>
      <Stack direction='row' alignItems='center' gap={2}>
        <SectionTitle title={displayName} />
        <DescriptionIcon description={description} />
      </Stack>
      {section && <Chip label={`${section}`} color={'secondary'} />}
    </div>
  );
}

interface ITextualKpiSectionProps {
  description: string | null;
  displayName: string;
  kpiSection: KpiSection;
  section: IKPITemplateSectionDataModel;
  frequency: KPIRequestFrequency;
  period: string;
  onChange: ITextualKpiFieldProps['onChange'];
  response: IKPIRequestResponse;
  disabled: boolean;
  rendererMeta?: IFormFieldTextMeta;
}

export function TextualKpiSection(props: ITextualKpiSectionProps) {
  const {
    description,
    displayName,
    kpiSection,
    section,
    frequency,
    period,
    onChange,
    response,
    disabled,
    rendererMeta = {},
  } = props;
  return (
    <div css={kpiWrapperCss}>
      <KpiSectionHeader description={description} displayName={displayName} section={kpiSection} />
      <TextualKpiField
        kpiRequestFrequency={frequency}
        onChange={onChange}
        period={period}
        section={section}
        response={response}
        disabled={disabled}
        rendererMeta={rendererMeta}
      />
    </div>
  );
}

function getSectionDescription(section: IKPITemplateSectionDataModel) {
  if ((section.meta as IField<unknown>)?.type == 'fileUpload') {
    return `Accepted formats include xlsx, xls, csv and pdf. Files must be less than ${MaxDataCollectionFileSize / 1024 / 1024}MB`;
  }
  return undefined;
}
